import React, { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { promotionsService } from '../../services/promotionsService';
import { Promotion, PromotionStatus } from 'public-contracts';
import { useTranslation } from '../../hooks/useTranslation';
import { AlertCircle, CheckCircle2, XCircle, Clock } from 'lucide-react';

interface PromotionStatusBadgeProps {
  promotionIds: string[];
  className?: string;
}

export const PromotionStatusBadge: React.FC<PromotionStatusBadgeProps> = ({
  promotionIds,
  className = '',
}) => {
  const { translate } = useTranslation();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPromotions = async () => {
      if (!promotionIds || promotionIds.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const promotionDetails = await Promise.all(
          promotionIds.map((id) => promotionsService.getPromotion(id))
        );
        setPromotions(promotionDetails.filter((p) => p !== null) as Promotion[]);
      } catch (error) {
        console.error('Error loading promotion details:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPromotions();
  }, [promotionIds]);

  if (loading || promotions.length === 0) {
    return null;
  }

  // Get the most critical status (REJECTED > PENDING_APPROVAL > ACTIVE)
  const getStatusPriority = (status: PromotionStatus): number => {
    switch (status) {
      case PromotionStatus.REJECTED:
        return 3;
      case PromotionStatus.PENDING_APPROVAL:
        return 2;
      case PromotionStatus.ACTIVE:
        return 1;
      default:
        return 0;
    }
  };

  const primaryPromotion = promotions.reduce((prev, current) => {
    return getStatusPriority(current.status) > getStatusPriority(prev.status)
      ? current
      : prev;
  });

  const getStatusBadge = (status: PromotionStatus) => {
    switch (status) {
      case PromotionStatus.REJECTED:
        return (
          <Badge
            variant="destructive"
            className={`flex items-center gap-1 ${className}`}
          >
            <XCircle className="h-3 w-3" />
            {translate(
              'pos.orders.promotion.status.rejected',
              'Promotion Rejected'
            )}
          </Badge>
        );
      case PromotionStatus.PENDING_APPROVAL:
        return (
          <Badge
            variant="outline"
            className={`flex items-center gap-1 border-yellow-500 text-yellow-700 ${className}`}
          >
            <Clock className="h-3 w-3" />
            {translate(
              'pos.orders.promotion.status.pending',
              'Pending Approval'
            )}
          </Badge>
        );
      case PromotionStatus.ACTIVE:
        return (
          <Badge
            variant="outline"
            className={`flex items-center gap-1 border-green-500 text-green-700 ${className}`}
          >
            <CheckCircle2 className="h-3 w-3" />
            {translate(
              'pos.orders.promotion.status.approved',
              'Approved'
            )}
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {getStatusBadge(primaryPromotion.status)}
      {promotions.length > 1 && (
        <Badge variant="outline" className={className}>
          +{promotions.length - 1} more
        </Badge>
      )}
    </div>
  );
};

